import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { lastValueFrom } from 'rxjs';
import { IncompleteWillReport } from '../models/IncompleteWillReport';
import { User } from '../Models/User';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.css'],
  standalone: false
})
export class ReportsComponent implements OnInit, AfterViewInit {
  @Input({ required: true }) loggedInUser?: User = undefined;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = ['probateId', 'deceasedName', 'fileName'];

  allRecords: IncompleteWillReport[] = [];
  dataSource = new MatTableDataSource<IncompleteWillReport>([]);

  pageSize = 50;

  probateIdSearch: string = '';
  nameSearch: string = '';

  private loggedInUserTag = '';

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private userService: UserService
  ) {}

  ngOnInit() {
    const user = this.userService.loggedInUser;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.loadData();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  async loadData() {
    try {
      const params = new HttpParams().set('user', this.loggedInUserTag);
      this.allRecords = await lastValueFrom(
        this.http.get<IncompleteWillReport[]>('/charitycentral/getincompletewills', { params })
      );
      this.applyFilters();
    } catch {
      this.snackBar.open('Failed to load incomplete wills report.', 'Close', { duration: 4000 });
    }
  }

  applyFilters() {
    const idTerm = this.probateIdSearch.trim().toLowerCase();
    const nameTerm = this.nameSearch.trim().toLowerCase();

    const filtered = this.allRecords.filter(r => {
      const matchesId = !idTerm || r.probateId.toString().includes(idTerm);
      const matchesName = !nameTerm || r.deceasedName.toLowerCase().includes(nameTerm);
      return matchesId && matchesName;
    });

    this.dataSource.data = filtered;
    if (this.paginator) this.paginator.firstPage();
  }

  clearSearch() {
    this.probateIdSearch = '';
    this.nameSearch = '';
    this.applyFilters();
  }

  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
  }

  downloadCsv() {
    const rows = this.dataSource.data;
    if (!rows.length) {
      this.snackBar.open('No data to download.', 'Close', { duration: 3000 });
      return;
    }

    const header = 'Probate ID,Deceased Name,File Name';
    const lines = rows.map(r =>
      `${r.probateId},"${(r.deceasedName ?? '').replace(/"/g, '""')}","${(r.fileName ?? '').replace(/"/g, '""')}"`
    );
    const csv = [header, ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incomplete_wills_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
